import React from 'react';
import { motion } from 'framer-motion';
import { Percent, CircleDollarSign, Check, TrendingUp, FileText, Clock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const BusinessModel = () => {
  const { t } = useLanguage();

  const benefits = [
    { icon: Check, text: t('businessModel.noSetup') },
    { icon: Check, text: t('businessModel.noManagement') },
    { icon: TrendingUp, text: t('businessModel.passive') },
    { icon: FileText, text: t('businessModel.transparent') },
  ];

  return (
    <section 
      id="pricing" 
      data-testid="business-model-section"
      className="py-24 md:py-32 relative overflow-hidden"
    >
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#0A0A0A] to-[#050505]" />
      
      {/* Orange Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#FF7A00]/8 rounded-full blur-[200px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-20"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-[#FF7A00]/10 text-[#FF7A00] text-sm tracking-widest uppercase mb-4">
            Business Model
          </span>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tight mb-4">
            {t('businessModel.title')}
          </h2>
          <p className="text-lg text-white/60 max-w-xl mx-auto">
            {t('businessModel.subtitle')}
          </p>
        </motion.div>

        {/* Main Numbers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {/* Commission Card */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            data-testid="commission-card"
            className="relative group"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[#FF7A00]/20 to-transparent rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            <div className="relative glass-card rounded-3xl p-10 md:p-12 text-center border-[#FF7A00]/30 group-hover:border-[#FF7A00]/50">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#FF7A00]/10 border border-[#FF7A00]/30 flex items-center justify-center">
                <Percent className="w-10 h-10 text-[#FF7A00]" />
              </div>
              <div className="mb-4">
                <span className="text-7xl md:text-9xl font-bold text-gradient">20</span>
                <span className="text-5xl md:text-6xl font-bold text-[#FF7A00]">%</span>
              </div>
              <p className="text-xl text-white/70">{t('businessModel.commission')}</p>
              <p className="text-sm text-white/40 mt-2">Sur chaque vente générée</p>
            </div>
          </motion.div>

          {/* Investment Card */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            data-testid="investment-card"
            className="relative group"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#10B981]/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
            <div className="relative glass-card rounded-3xl p-10 md:p-12 text-center border-[#10B981]/30 group-hover:border-[#10B981]/50">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#10B981]/10 border border-[#10B981]/30 flex items-center justify-center">
                <CircleDollarSign className="w-10 h-10 text-[#10B981]" />
              </div>
              <div className="mb-4">
                <span className="text-7xl md:text-9xl font-bold text-[#10B981]">0</span>
                <span className="text-5xl md:text-6xl font-bold text-[#10B981]">€</span>
              </div>
              <p className="text-xl text-white/70">{t('businessModel.investment')}</p>
              <p className="text-sm text-white/40 mt-2">Zéro coût initial</p>
            </div>
          </motion.div>
        </div>

        {/* Benefits Grid */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <div 
                key={index}
                data-testid={`benefit-${index}`}
                className="glass-card rounded-2xl p-6 flex items-center gap-4 hover:border-[#FF7A00]/30 transition-colors"
              >
                <div className="w-10 h-10 rounded-xl bg-[#FF7A00]/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-[#FF7A00]" />
                </div>
                <span className="text-white/80 text-sm font-medium">{benefit.text}</span>
              </div>
            );
          })}
        </motion.div>

        {/* How Pricing Works */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12 glass-card rounded-3xl p-8 md:p-10"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <Clock className="w-8 h-8 text-[#FF7A00]" />
              <div>
                <h4 className="text-xl font-semibold text-white">Comment ça fonctionne</h4>
                <p className="text-white/60">Le client paie sa photo directement via l'application</p>
              </div>
            </div>
            <div className="flex items-center gap-4 px-6 py-3 rounded-full bg-white/5 border border-white/10">
              <span className="text-white/60">Prix photo:</span>
              <span className="text-2xl font-bold text-[#FF7A00]">5-15€</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BusinessModel;
